import React, { useEffect, useState } from "react";
import {
    Bot,
    Sparkles,
    Loader2,
    Trash2,
    Eye,
    Send,
    Check,
    X,
    RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const STATUS_LABELS = {
    pending_review: { label: "İncelemede", color: "bg-amber-100 text-amber-700" },
    approved: { label: "Onaylandı", color: "bg-data-green/10 text-data-green" },
    rejected: { label: "Reddedildi", color: "bg-data-red/10 text-data-red" },
    published: { label: "Yayında", color: "bg-teal/10 text-teal-700" },
};

export default function AIDrafts() {
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [filter, setFilter] = useState("");
    const [showGen, setShowGen] = useState(false);
    const [rawText, setRawText] = useState("");
    const [sourceUrl, setSourceUrl] = useState("");
    const [sourceName, setSourceName] = useState("");
    const [activeDraft, setActiveDraft] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = filter ? `?status=${filter}` : "";
            const { data } = await api.get(`/admin/ai-news/drafts${params}`);
            setDrafts(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [filter]);

    const generate = async () => {
        if (rawText.trim().length < 80) {
            toast.error("En az 80 karakter ham metin girin.");
            return;
        }
        setGenerating(true);
        try {
            const { data } = await api.post("/admin/ai-news/generate", {
                raw_text: rawText,
                source_url: sourceUrl || null,
                source_name: sourceName || null,
            });
            toast.success("AI taslağı oluşturuldu.");
            setShowGen(false);
            setRawText("");
            setSourceUrl("");
            setSourceName("");
            await load();
            // Open the newly created draft for review
            const newDraft = await api.get(`/admin/ai-news/drafts/${data.id}`);
            setActiveDraft(newDraft.data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setGenerating(false);
        }
    };

    const updateDraft = async (id, draft, status = null) => {
        try {
            await api.put(`/admin/ai-news/drafts/${id}`, { draft, status });
            toast.success("Kaydedildi.");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const publish = async (id) => {
        if (!window.confirm("Bu taslağı blog'a yayınlamak istediğinizden emin misiniz?")) return;
        try {
            const { data } = await api.post(`/admin/ai-news/drafts/${id}/publish`, {});
            toast.success(`Yayınlandı: /blog/${data.slug}`);
            setActiveDraft(null);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const removeDraft = async (id) => {
        if (!window.confirm("Taslak silinecek. Onaylıyor musunuz?")) return;
        try {
            await api.delete(`/admin/ai-news/drafts/${id}`);
            toast.success("Silindi.");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div data-testid="admin-ai-drafts-page">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
                <div>
                    <h1 className="text-2xl font-bold text-dark-navy inline-flex items-center gap-2">
                        <Bot className="w-6 h-6 text-teal" /> AI Taslakları
                    </h1>
                    <p className="text-sm text-deep-blue/70 mt-1">
                        Ham metni Claude Sonnet 4.5'a göndererek TR/EN/DE çoklu dilli haber taslakları üret, incele, blog'a yayınla.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="px-3 py-2 rounded-md border border-slate-300 bg-white text-sm"
                        data-testid="ai-drafts-filter"
                    >
                        <option value="">Tümü</option>
                        <option value="pending_review">İncelemede</option>
                        <option value="approved">Onaylı</option>
                        <option value="rejected">Reddedildi</option>
                        <option value="published">Yayında</option>
                    </select>
                    <button onClick={load} className="p-2 rounded-md bg-white border border-slate-300 hover:bg-light-bg" title="Yenile">
                        <RefreshCw className="w-4 h-4 text-deep-blue" />
                    </button>
                    <button
                        onClick={() => setShowGen(true)}
                        className="bg-gold hover:bg-gold-700 text-dark-navy text-sm font-bold px-4 py-2 rounded-md inline-flex items-center gap-2"
                        data-testid="ai-drafts-new-btn"
                    >
                        <Sparkles className="w-4 h-4" /> Yeni AI Taslak
                    </button>
                </div>
            </div>

            {showGen && (
                <div className="bg-white rounded-lg shadow-md border border-slate-100 p-5 mb-5" data-testid="ai-drafts-gen-form">
                    <h2 className="text-base font-semibold text-dark-navy mb-3">Yeni AI Haber Taslağı Üret</h2>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-semibold text-deep-blue/80 uppercase tracking-wider">Ham Metin (en az 80 karakter)</label>
                            <textarea
                                rows={10}
                                value={rawText}
                                onChange={(e) => setRawText(e.target.value)}
                                placeholder="Buraya ham haber metnini yapıştırın (TR/EN/DE herhangi biri olabilir)..."
                                className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm font-mono outline-none focus:border-teal"
                                data-testid="ai-drafts-raw-text"
                            />
                            <span className="text-xs text-deep-blue/60 mt-1 block">
                                Karakter: <strong>{rawText.length}</strong>
                            </span>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <input
                                type="url"
                                placeholder="Kaynak URL (opsiyonel)"
                                value={sourceUrl}
                                onChange={(e) => setSourceUrl(e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-teal"
                                data-testid="ai-drafts-source-url"
                            />
                            <input
                                type="text"
                                placeholder="Kaynak Adı (opsiyonel)"
                                value={sourceName}
                                onChange={(e) => setSourceName(e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-teal"
                                data-testid="ai-drafts-source-name"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={generate}
                                disabled={generating}
                                className="bg-teal hover:bg-teal-700 text-dark-navy text-sm font-bold px-5 py-2 rounded-md inline-flex items-center gap-2 disabled:opacity-60"
                                data-testid="ai-drafts-generate-btn"
                                aria-busy={generating}
                            >
                                {generating ? (
                                    <span data-testid="ai-drafts-generating" className="inline-flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" /> AI üretiyor...
                                    </span>
                                ) : (
                                    <>
                                        <Sparkles className="w-4 h-4" /> AI ile Üret
                                    </>
                                )}
                            </button>
                            <button onClick={() => setShowGen(false)} className="text-sm text-deep-blue hover:text-dark-navy font-semibold">
                                İptal
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-lg shadow-md border border-slate-100 overflow-hidden">
                {loading ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12 inline-flex items-center justify-center gap-2 w-full">
                        <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...
                    </div>
                ) : drafts.length === 0 ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12">
                        Henüz AI taslağı yok. <strong>Yeni AI Taslak</strong> butonuna tıklayın.
                    </div>
                ) : (
                    <table className="w-full text-sm" data-testid="ai-drafts-table">
                        <thead>
                            <tr className="bg-light-bg text-left text-xs uppercase tracking-wider text-deep-blue/70">
                                <th className="px-4 py-3">Başlık (TR)</th>
                                <th className="px-4 py-3">Kaynak</th>
                                <th className="px-4 py-3">Skor</th>
                                <th className="px-4 py-3">Etiketler</th>
                                <th className="px-4 py-3">Durum</th>
                                <th className="px-4 py-3 w-44">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {drafts.map((d) => {
                                const draft = d.draft || {};
                                const tags = draft.tags || [];
                                const status = STATUS_LABELS[d.status] || { label: d.status, color: "bg-slate-100 text-slate-600" };
                                return (
                                    <tr key={d.id} className="border-t border-slate-100" data-testid={`ai-draft-row-${d.id}`}>
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-dark-navy text-sm line-clamp-2">{(draft.title || {}).tr || "Başlıksız"}</div>
                                            <div className="text-xs text-deep-blue/60 mt-0.5">{new Date(d.created_at).toLocaleDateString("tr-TR")}</div>
                                        </td>
                                        <td className="px-4 py-3 text-xs">
                                            {d.source_name && <div className="font-semibold text-deep-blue">{d.source_name}</div>}
                                            {d.source_url && (
                                                <a href={d.source_url} target="_blank" rel="noopener noreferrer" className="text-teal-700 hover:underline truncate block max-w-[180px]">
                                                    {d.source_url}
                                                </a>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {d.relevance_score != null ? (
                                                <span className={`inline-block px-2 py-1 rounded text-[11px] font-bold font-data ${d.relevance_score >= 70 ? "bg-data-green/15 text-data-green" : d.relevance_score >= 40 ? "bg-amber-100 text-amber-700" : "bg-data-red/10 text-data-red"}`}>
                                                    {d.relevance_score}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-deep-blue/40">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-xs">
                                            <div className="flex flex-wrap gap-1">
                                                {tags.slice(0, 3).map((t) => (
                                                    <span key={t} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px]">{t}</span>
                                                ))}
                                                {tags.length > 3 && <span className="text-deep-blue/60">+{tags.length - 3}</span>}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${status.color}`}>{status.label}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={async () => {
                                                        const r = await api.get(`/admin/ai-news/drafts/${d.id}`);
                                                        setActiveDraft(r.data);
                                                    }}
                                                    title="İncele"
                                                    className="p-1.5 rounded-md hover:bg-light-bg"
                                                    data-testid={`ai-draft-view-${d.id}`}
                                                >
                                                    <Eye className="w-4 h-4 text-deep-blue" />
                                                </button>
                                                {d.status !== "published" && (
                                                    <button
                                                        onClick={() => publish(d.id)}
                                                        title="Blog'a Yayınla"
                                                        className="p-1.5 rounded-md hover:bg-light-bg"
                                                        data-testid={`ai-draft-publish-${d.id}`}
                                                    >
                                                        <Send className="w-4 h-4 text-data-green" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => removeDraft(d.id)}
                                                    title="Sil"
                                                    className="p-1.5 rounded-md hover:bg-light-bg"
                                                >
                                                    <Trash2 className="w-4 h-4 text-data-red" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {activeDraft && (
                <DraftEditor
                    draft={activeDraft}
                    onClose={() => setActiveDraft(null)}
                    onSave={(d, status) => updateDraft(activeDraft.id, d, status).then(() => setActiveDraft({ ...activeDraft, draft: d, status: status || activeDraft.status }))}
                    onPublish={() => publish(activeDraft.id)}
                />
            )}
        </div>
    );
}

function DraftEditor({ draft, onClose, onSave, onPublish }) {
    const [d, setD] = useState(draft.draft || {});

    const updateField = (path, value) => {
        setD((p) => {
            const next = JSON.parse(JSON.stringify(p));
            const segs = path.split(".");
            let o = next;
            for (let i = 0; i < segs.length - 1; i++) o = o[segs[i]] || (o[segs[i]] = {});
            o[segs[segs.length - 1]] = value;
            return next;
        });
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="ai-draft-editor">
            <div className="bg-white rounded-lg shadow-2xl border border-slate-200 max-w-3xl w-full my-8">
                <div className="flex items-center justify-between p-5 border-b border-slate-200">
                    <h2 className="text-base font-semibold text-dark-navy">AI Taslak — İncele & Düzenle</h2>
                    <button onClick={onClose} className="p-1.5 rounded-md hover:bg-light-bg" data-testid="ai-draft-editor-close">
                        <X className="w-4 h-4 text-deep-blue" />
                    </button>
                </div>
                <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                    {["tr", "en", "de"].map((lang) => (
                        <div key={lang} className="border border-slate-200 rounded-md p-4">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-deep-blue/80 mb-3">{lang.toUpperCase()}</h3>
                            <input
                                type="text"
                                placeholder="Başlık"
                                value={(d.title || {})[lang] || ""}
                                onChange={(e) => updateField(`title.${lang}`, e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm mb-2"
                            />
                            <textarea
                                rows={2}
                                placeholder="Özet"
                                value={(d.excerpt || {})[lang] || ""}
                                onChange={(e) => updateField(`excerpt.${lang}`, e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm mb-2"
                            />
                            <textarea
                                rows={6}
                                placeholder="HTML içerik"
                                value={(d.content_html || {})[lang] || ""}
                                onChange={(e) => updateField(`content_html.${lang}`, e.target.value)}
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm font-mono"
                            />
                        </div>
                    ))}
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">Etiketler (virgülle ayır)</label>
                        <input
                            type="text"
                            value={(d.tags || []).join(", ")}
                            onChange={(e) => updateField("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
                            className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                        />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <input
                            placeholder="Meta Title"
                            value={(d.seo || {}).meta_title || ""}
                            onChange={(e) => updateField("seo.meta_title", e.target.value)}
                            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                        />
                        <input
                            placeholder="Meta Desc"
                            value={(d.seo || {}).meta_desc || ""}
                            onChange={(e) => updateField("seo.meta_desc", e.target.value)}
                            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                        />
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-200">
                    <button onClick={() => onSave(d, "rejected")} className="px-4 py-2 rounded-md bg-light-bg text-data-red text-sm font-semibold hover:bg-data-red/10">
                        <X className="w-4 h-4 inline mr-1" /> Reddet
                    </button>
                    <button onClick={() => onSave(d)} className="px-4 py-2 rounded-md bg-deep-blue hover:bg-dark-navy text-white text-sm font-semibold">
                        Kaydet
                    </button>
                    <button
                        onClick={() => onSave(d, "approved")}
                        className="px-4 py-2 rounded-md bg-teal hover:bg-teal-700 text-dark-navy text-sm font-bold inline-flex items-center gap-1"
                    >
                        <Check className="w-4 h-4" /> Onayla
                    </button>
                    <button
                        onClick={onPublish}
                        className="px-4 py-2 rounded-md bg-gold hover:bg-gold-700 text-dark-navy text-sm font-bold inline-flex items-center gap-1"
                        data-testid="ai-draft-publish-modal"
                    >
                        <Send className="w-4 h-4" /> Blog'a Yayınla
                    </button>
                </div>
            </div>
        </div>
    );
}
